import { catalog } from "../i18n.js";

/** A number as Indonesian writes it: a decimal comma. */
const decimalComma = (value: string | number): string => String(value).replace(".", ",");

/** Signing in, and the screens the workspace shows while it starts (components/Login.tsx). */
export const LOGIN = catalog(
  {
    language: "Language",
    couldNotSignIn: "Could not sign in.",
    // The hero's headline, in pieces around its line break and its highlighted words.
    heroFirstLine: "Bots that take",
    heroBefore: "work ",
    heroEmphasis: "off your hands",
    heroAfter: ".",
    heroAbout: "Chat with them, give them routines, connect the apps you use, and approve anything that reaches outside your workspace.",
    brandDemo: "ark-bot · hosted demo",
    ownWorkspace: "Your own workspace",
    welcomeBack: "Welcome back",
    createAbout: "Create an account and we’ll start a workspace for you on this PC. No email or verification required.",
    signInAbout: "Sign in to return to your bots, files and workspace.",
    email: "Email",
    emailExample: "you@example.com",
    password: "Password",
    workspace: "Workspace",
    heavy: "Heavy · full desktop",
    heavyAbout: (gigabytes: number) => `Chat, browser & computer · ${gigabytes} GB RAM`,
    lite: "Lite",
    liteAbout: (gigabytes: number) => `Chat, terminal & files · ${gigabytes} GB RAM`,
    pleaseWait: "Please wait…",
    createAccount: "Create account",
    signIn: "Sign in",
    haveAccount: "Already have an account? Sign in",
    newHere: "New here? Create an account",
    newHerePortal: "New here? Create your account in the Ark Platform portal →",
    demoFootnote: "Demo workspaces share this PC’s configured AI connection.",
    cloudFootnote: "Your workspace runs in the cloud on your Ark Bot subscription.",

    cannotReachService: "Cannot reach the service.",
    couldNotStart: "Could not start your workspace.",
    operationFailed: "The operation failed.",
    connecting: "Connecting to your workspace…",
    retryConnection: "Retry connection",
    needsPlan: "Ark Bot needs an active plan",
    plansInPortal: "Plans and billing are in the Ark Platform portal. Once you’ve subscribed, come back here and your workspace starts on its own.",
    getPlan: "Get a plan in the portal",
    checkAgain: "I’ve subscribed, check again",
    signedInAs: (username: string) => `Signed in as ${username}.`,
    logOut: "Log out",
    fullDesktop: "Full desktop",
    liteWorkspace: "Lite workspace",
    applyingPlan: "Applying your plan",
    restarting: "Restarting your workspace",
    needsAttention: "Workspace needs attention",
    starting: "Starting your workspace",
    lastStartFailed: "The last start didn’t finish. Try again.",
    backingUp: "Backing up and restarting your workspace…",
    startingAbout: "Your workspace is starting. This can take a minute — you’ll enter automatically when it’s ready.",
    demoRam: (used: number, total: number) => `Demo RAM: ${used} / ${total} GB reserved`,
    inProgress: "Workspace operation in progress",
    tryAgain: "Try again",
  },
  {
    language: "Bahasa",
    couldNotSignIn: "Tidak bisa masuk.",
    heroFirstLine: "Bot yang siap",
    heroBefore: "",
    heroEmphasis: "mengambil alih kerjaanmu",
    heroAfter: ".",
    heroAbout: "Chat dengan mereka, beri rutinitas, hubungkan aplikasi yang kamu pakai, dan setujui apa pun yang keluar dari ruang kerjamu.",
    brandDemo: "ark-bot · demo hosted",
    ownWorkspace: "Ruang kerja milikmu",
    welcomeBack: "Selamat datang kembali",
    createAbout: "Buat akun dan kami akan menyiapkan ruang kerja untukmu di PC ini. Tanpa email atau verifikasi.",
    signInAbout: "Masuk untuk kembali ke bot, file, dan ruang kerjamu.",
    email: "Email",
    emailExample: "kamu@contoh.com",
    password: "Kata sandi",
    workspace: "Ruang kerja",
    heavy: "Heavy · desktop lengkap",
    heavyAbout: (gigabytes: number) => `Chat, browser & komputer · ${decimalComma(gigabytes)} GB RAM`,
    lite: "Lite",
    liteAbout: (gigabytes: number) => `Chat, terminal & file · ${decimalComma(gigabytes)} GB RAM`,
    pleaseWait: "Tunggu sebentar…",
    createAccount: "Buat akun",
    signIn: "Masuk",
    haveAccount: "Sudah punya akun? Masuk",
    newHere: "Baru di sini? Buat akun",
    newHerePortal: "Baru di sini? Buat akunmu di portal Ark Platform →",
    demoFootnote: "Ruang kerja demo memakai koneksi AI yang diatur di PC ini bersama-sama.",
    cloudFootnote: "Ruang kerjamu berjalan di cloud dengan langganan Ark Bot kamu.",

    cannotReachService: "Tidak bisa terhubung ke layanan.",
    couldNotStart: "Ruang kerjamu tidak bisa dijalankan.",
    operationFailed: "Terjadi kesalahan.",
    connecting: "Menghubungkan ke ruang kerjamu…",
    retryConnection: "Coba hubungkan lagi",
    needsPlan: "Ark Bot butuh paket yang aktif",
    plansInPortal: "Paket dan tagihan ada di portal Ark Platform. Setelah berlangganan, kembali ke sini dan ruang kerjamu akan menyala sendiri.",
    getPlan: "Pilih paket di portal",
    checkAgain: "Aku sudah berlangganan, cek lagi",
    signedInAs: (username: string) => `Masuk sebagai ${username}.`,
    logOut: "Keluar",
    fullDesktop: "Desktop lengkap",
    liteWorkspace: "Ruang kerja Lite",
    applyingPlan: "Menerapkan paketmu",
    restarting: "Memulai ulang ruang kerjamu",
    needsAttention: "Ruang kerja perlu dicek",
    starting: "Menyalakan ruang kerjamu",
    lastStartFailed: "Percobaan terakhir tidak selesai. Coba lagi.",
    backingUp: "Mencadangkan dan memulai ulang ruang kerjamu…",
    startingAbout: "Ruang kerjamu sedang dinyalakan. Ini bisa makan waktu semenit — kamu akan masuk otomatis saat sudah siap.",
    demoRam: (used: number, total: number) => `RAM demo: ${decimalComma(used)} / ${decimalComma(total)} GB terpakai`,
    inProgress: "Ruang kerja sedang diproses",
    tryAgain: "Coba lagi",
  },
);

/** The connect screen of the packaged app (components/Connect.tsx). */
export const CONNECT = catalog(
  {
    noAnswerIn: (seconds: number) => `no answer in ${seconds} s`,
    noConnection: "no connection",
    notAnAddress: (example: string) => `That is not an address. Something like ${example}.`,
    answered: (status: number) => `it answered ${status}`,
    answeredNotArkBot: (status: number) => `it answered ${status} with something other than ark-bot's`,
    couldNotReach: (host: string, reason: string) => `Could not reach ${host}: ${reason}. Is the host started and up to date, and is this phone on its network, or on Tailscale?`,
    whereIsIt: "Where is your ark-bot?",
    host: "Host",
    close: "Close",
    about: "The hosted app is filled in. For a box on your own computer, type the address the host prints when it starts instead: https and port 7702 with a Tailscale certificate, or http and port 7701 on the same network.",
    address: "Address",
    hostAddress: "Host address",
    trying: "Trying…",
    connect: "Connect",
    keepAnyway: "Keep it anyway",
    cancel: "Cancel",
  },
  {
    noAnswerIn: (seconds: number) => `tidak ada jawaban dalam ${seconds} detik`,
    noConnection: "tidak ada koneksi",
    notAnAddress: (example: string) => `Itu bukan alamat. Contohnya ${example}.`,
    answered: (status: number) => `jawabannya ${status}`,
    answeredNotArkBot: (status: number) => `jawabannya ${status}, dan bukan dari ark-bot`,
    couldNotReach: (host: string, reason: string) => `Tidak bisa terhubung ke ${host}: ${reason}. Apakah host sudah menyala dan versinya terbaru, dan apakah ponsel ini ada di jaringannya, atau di Tailscale?`,
    whereIsIt: "Di mana ark-bot kamu?",
    host: "Host",
    close: "Tutup",
    about: "Aplikasi hosted sudah terisi. Untuk box di komputermu sendiri, ketik alamat yang ditampilkan host saat menyala: https dan port 7702 dengan sertifikat Tailscale, atau http dan port 7701 di jaringan yang sama.",
    address: "Alamat",
    hostAddress: "Alamat host",
    trying: "Mencoba…",
    connect: "Hubungkan",
    keepAnyway: "Simpan saja",
    cancel: "Batal",
  },
);

/** Messages the client writes itself when the service answers without one (hosted-api.ts). */
export const HOSTED_API = catalog(
  {
    requestFailed: (status: number) => `Request failed (${status}).`,
  },
  {
    requestFailed: (status: number) => `Permintaan gagal (${status}).`,
  },
);
